import { forwardRef } from 'react';

import {
    CHANGE_HISTORY_TABLE_HEADERS_WIDTHS,
    ChangeHistoryTableHeaders,
    ChangeHistoryType,
} from 'utils/constants/admin';

import { TableCell, TableRow, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { User } from 'components/Admin/Invoices/InvoicesTable/Invoice/User';
import { EventValue, EventValueKind } from 'components/common/ChangeHistoryModal/components/EventValue/EventValue';

import { EventType } from 'store/models/changeHistory';

interface ChangeHistoryEventProps {
    event: EventType;
    historyType: ChangeHistoryType;
}

export const ChangeHistoryEvent = forwardRef<HTMLTableRowElement, ChangeHistoryEventProps>(
    ({ event, historyType }, ref) => {
        const {
            userId,
            userName,
            realUserId,
            realUserName,
            userIpAddress,
            changeTime,
            fieldName,
            oldValue,
            newValue,
            addedValues,
            removedValues,
            changeSource,
        } = event;

        const trls = useTrls();
        const trlKey = `campaign.info.sudo.actions.changeHistory.field.${historyType}.${String(fieldName)}`;

        return (
            <TableRow ref={ref} align="left">
                <TableCell width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[ChangeHistoryTableHeaders.User]}>
                    <Text typography="label-4-regular">
                        {userId ? <User userId={userId} userName={userName} /> : '−'}
                    </Text>
                    <VSpacing default={2} />
                    <Text style="secondary" typography="label-4-regular">
                        {realUserName && realUserId ? <User userId={realUserId} userName={realUserName} /> : '—'}
                    </Text>
                </TableCell>
                <TableCell width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[ChangeHistoryTableHeaders.Date]}>
                    <Text typography="label-4-regular">{changeTime}</Text>
                    <VSpacing default={2} />
                    <Text style="secondary" typography="label-4-regular">
                        {userIpAddress}
                    </Text>
                </TableCell>
                <TableCell width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[ChangeHistoryTableHeaders.Field]}>
                    <Text typography="label-4-regular" className="history-event__field">
                        {trls(trlKey)}
                    </Text>
                </TableCell>
                <TableCell width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[ChangeHistoryTableHeaders.OldValue]}>
                    <EventValue fieldName={fieldName} value={oldValue ?? ''} kind={EventValueKind.Old} />
                </TableCell>
                <TableCell width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[ChangeHistoryTableHeaders.NewValue]}>
                    {removedValues && (
                        <EventValue fieldName={fieldName} value={removedValues} kind={EventValueKind.Removed} />
                    )}
                    {addedValues && (
                        <EventValue fieldName={fieldName} value={addedValues} kind={EventValueKind.Added} />
                    )}
                    <EventValue fieldName={fieldName} value={newValue ?? ''} kind={EventValueKind.New} />
                </TableCell>
                <TableCell width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[ChangeHistoryTableHeaders.ChangeSource]}>
                    <Text typography="label-4-regular">{changeSource ?? '—'}</Text>
                </TableCell>
            </TableRow>
        );
    }
);

ChangeHistoryEvent.displayName = 'ChangeHistoryEvent';
